import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Box } from '@mui/material';

/* =======================
   Top Rounded Bar Shape
   ======================= */

const TopRoundedBar = ({ x, y, width, height, radius, fill }) => {
  const r = Math.min(radius, width / 2, height);

  const d = `
    M ${x} ${y + r}
    Q ${x} ${y} ${x + r} ${y}
    L ${x + width - r} ${y}
    Q ${x + width} ${y} ${x + width} ${y + r}
    L ${x + width} ${y + height}
    L ${x} ${y + height}
    Z
  `;

  return <path d={d} fill={fill} />;
};

/* =======================
   Factory: Gradient Bar
   ======================= */

const GradientBarShape =
  ({ gradient, radius }) =>
  ({ x, y, width, height }) => {
    const gradientId = `${gradient.id}-${y}`;

    return (
      <g>
        <defs>
          <linearGradient
            id={gradientId}
            x1="0"
            y1={y}
            x2="0"
            y2={y + height}
            gradientUnits="userSpaceOnUse"
          >
            {gradient.stops.map((stop, i) => (
              <stop
                key={i}
                offset={stop.offset}
                stopColor={stop.color}
                stopOpacity={stop.opacity ?? 1}
              />
            ))}
          </linearGradient>
        </defs>

        <TopRoundedBar
          x={x}
          y={y}
          width={width}
          height={height}
          radius={radius}
          fill={`url(#${gradientId})`}
        />
      </g>
    );
  };

/* =======================
   Reusable Chart
   ======================= */

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const { value, details } = payload[0].payload;

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: 1,
        boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
        px: 1.5,
        py: 1
      }}
    >
      <Box sx={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{label}</Box>

      <Box sx={{ fontSize: 12, color: '#374151', mt: 0.5 }}>
        Value: <strong>{value}</strong>
      </Box>

      {details && (
        <Box
          sx={{
            fontSize: 11,
            color: '#6B7280',
            mt: 0.5,
            maxWidth: 200
          }}
        >
          {details}
        </Box>
      )}
    </Box>
  );
};

export default function ReusableBarChart({
  data,
  height = 300,
  xKey = 'label',
  bars = [],
  barWidth = 40,
  barGap = 2,
  barCategoryGap = 10,
  gridColor = '#E5E7EB',
  gridDash = '3 3'
}) {
  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={data} barGap={barGap} barCategoryGap={barCategoryGap}>
          <CartesianGrid vertical={false} stroke={gridColor} strokeDasharray={gridDash} />

          <XAxis
            dataKey={xKey}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6B7280', fontSize: 11, fontWeight: 700 }}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            domain={[0, 'dataMax']}
            tick={{ fill: '#6B7280', fontSize: 11, fontWeight: 700 }}
          />

          <Tooltip content={<CustomTooltip />} />

          {/* SINGLE BAR – MULTIPLE COLORS */}
          <Bar
            dataKey="value"
            maxBarSize={barWidth}
            shape={(props) => {
              const barConfig = bars[props.index];
              if (!barConfig) return null;

              return GradientBarShape({
                gradient: barConfig.gradient,
                radius: barConfig.radius ?? barWidth / 2
              })(props);
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
